import { kioskLocation } from "../App/kioskRoutes";
import { useState, useEffect, useRef } from "react";
import Backend from "../utils/api.js";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import pinpointLogo from "../assets/pinpoint_no_bg.svg";
import KioskSwipeInstructionSVG from "../assets/KioskSwipeInstructionSVG";
import { useDocumentTitle } from "../utils/useDocumentTitle";
import useVersion from "../hooks/useVersion.jsx";

import {
  Box,
  Button,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Image,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Settings } from "lucide-react";
import Validators from "../utils/validators.jsx";
import SwipeOrReservePopup from "../Components/SwipeOrReservePopup.jsx";
import SwipeOutReminderPopup from "../Components/SwipeOutReminderPopup.jsx";
import { useAnimationToggle } from "../Contexts/AnimationContext.jsx";
import { useKioskToast } from "../Components/KioskToast.jsx";
import KioskLoadingAnimation from "../Components/KioskLoadingAnimation.jsx";

const DEV_TEST_USERS = [
  { uid: "817500000", swipeNum: "999999990001000", label: "Super Admin (adminfirst)" },
  { uid: "111100000", swipeNum: "999999990002000", label: "Space admin (labadminfirst)" },
  { uid: "222200000", swipeNum: "999999990003000", label: "Regular User (userfirst)" },
];
const DEV_DEFAULT_USER = DEV_TEST_USERS[2];

const devInputStyle = {
  borderWidth: "1.5px",
  borderColor: "gray.200",
  borderRadius: "md",
  _focus: { borderColor: "#EC2227", boxShadow: "0 0 0 1px #EC2227" },
};
const devButtonGradient = "linear(to-r, #EC2227, #F15B27)";

function DevToolsPanel({ isLoading, onSimulate }) {
  const [isOpen, setIsOpen] = useState(false);
  const [simMode, setSimMode] = useState("swipeNum");
  const [inputValue, setInputValue] = useState(DEV_DEFAULT_USER.swipeNum);

  function handleModeChange(val) {
    setSimMode(val);
    setInputValue(val === "swipeNum" ? DEV_DEFAULT_USER.swipeNum : DEV_DEFAULT_USER.uid);
  }

  function handleSimulate() {
    if (!inputValue) return;
    onSimulate(simMode, inputValue);
  }

  return (
    <Box
      position="fixed"
      bottom={4}
      right={4}
      zIndex={1000}
      onClick={(e) => e.stopPropagation()}
    >
      <Collapse in={isOpen}>
        <Box
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          boxShadow="md"
          p={5}
          mb={2}
          w="340px"
        >
          <Text fontSize="sm" fontWeight="700" color="gray.800" mb={4}>
            Swipe Simulator
          </Text>
          <RadioGroup onChange={handleModeChange} value={simMode}>
            <Flex gap={4} mb={3}>
              <Radio value="swipeNum" colorScheme="red" size="sm">
                <Text fontSize="sm" color="gray.800">Swipe Number</Text>
              </Radio>
              <Radio value="uid" colorScheme="red" size="sm">
                <Text fontSize="sm" color="gray.800">UID (skip conversion)</Text>
              </Radio>
            </Flex>
          </RadioGroup>
          <Select
            size="sm"
            value={inputValue}
            mb={3}
            {...devInputStyle}
            onChange={(e) => setInputValue(e.target.value)}
          >
            {DEV_TEST_USERS.map((user) => (
              <option key={user.uid} value={simMode === "swipeNum" ? user.swipeNum : user.uid}>
                {user.label}
              </option>
            ))}
          </Select>
          <Input
            size="sm"
            placeholder={simMode === "swipeNum" ? "Swipe number..." : "Or enter UID..."}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSimulate(); }}
            mb={4}
            {...devInputStyle}
          />
          <Button
            w="full"
            h="40px"
            bgGradient={devButtonGradient}
            color="white"
            borderRadius="md"
            fontSize="md"
            fontWeight="700"
            onClick={handleSimulate}
            isLoading={isLoading}
            _hover={{ bgGradient: devButtonGradient, boxShadow: "md" }}
            _active={{ bgGradient: devButtonGradient }}
          >
            Simulate
          </Button>
        </Box>
      </Collapse>
      <Flex justify="flex-end">
        <Button
          variant="unstyled"
          display="flex"
          alignItems="center"
          h="40px"
          pl={8}
          pr={8}
          onClick={() => setIsOpen(!isOpen)}
          bg="white"
          color="gray.700"
          border="1.5px solid"
          borderColor="gray.300"
          borderRadius="md"
          boxShadow="sm"
          fontSize="md"
          fontWeight="500"
          _hover={{ borderColor: "gray.400", boxShadow: "md" }}
          _active={{ bg: "gray.50" }}
        >
          {isOpen ? "▾ Dev Tools" : "▸ Dev Tools"}
        </Button>
      </Flex>
    </Box>
  );
}

// Lets lab staff pick which check-in method(s) the instructional animation
// demonstrates — useful when a kiosk's hardware only has one reader (e.g.
// no card slot). At least one method must stay enabled at all times, so
// each Switch is disabled when it's the only one left on.
function KioskAnimationSettings({ qrScanEnabled, setQrScanEnabled, cardScanEnabled, setCardScanEnabled, bottom }) {
  return (
    <Box
      position="fixed"
      bottom={bottom}
      right={4}
      zIndex={1000}
      onClick={(e) => e.stopPropagation()}
    >
      <Popover placement="top-end">
        <PopoverTrigger>
          <IconButton
            aria-label="Kiosk animation settings"
            icon={<Settings size={20} aria-hidden="true" />}
            isRound
            size="lg"
            bg="white"
            color="gray.600"
            border="1.5px solid"
            borderColor="gray.300"
            boxShadow="sm"
            _hover={{ borderColor: "gray.400", boxShadow: "md" }}
          />
        </PopoverTrigger>
        <PopoverContent w="260px">
          <PopoverArrow />
          <PopoverHeader fontWeight="700" fontSize="sm" color="gray.800">
            Animation settings
          </PopoverHeader>
          <PopoverBody>
            <Stack spacing={4}>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel htmlFor="qr-scan-toggle" mb={0} fontSize="sm" color="gray.700">
                  QR code scanning
                </FormLabel>
                <Switch
                  id="qr-scan-toggle"
                  colorScheme="red"
                  isChecked={qrScanEnabled}
                  isDisabled={qrScanEnabled && !cardScanEnabled}
                  onChange={(e) => setQrScanEnabled(e.target.checked)}
                />
              </FormControl>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel htmlFor="card-scan-toggle" mb={0} fontSize="sm" color="gray.700">
                  Card scanning
                </FormLabel>
                <Switch
                  id="card-scan-toggle"
                  colorScheme="red"
                  isChecked={cardScanEnabled}
                  isDisabled={cardScanEnabled && !qrScanEnabled}
                  onChange={(e) => setCardScanEnabled(e.target.checked)}
                />
              </FormControl>
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
}

/*
  Login page for users to swipe their university ID card and be routed to the tool reservation page.
  This page is used as the way for labs to authenticate users in-person via swipe-ins.
*/
function KioskLogin({ onSubmit, routeFunc, history, currLab, setCurrLab }) {
  const isNonProduction = window.location.hostname !== "pinpoint.umd.edu";
  const currLabName = (currLab?.id !== "Error" && currLab?.name) || "-------";
  const [isLoading, setIsLoading] = useState(false);

  const [input, setInput] = useState("");
  const [showChoicePopup, setShowChoicePopup] = useState(false);
  const [showReminderPopup, setShowReminderPopup] = useState(false);
  const [reminderCount, setReminderCount] = useState(1);
  const [UID, setUID] = useState("");

  const { version, environment } = useVersion();

  const { show: showToast, toastElements } = useKioskToast();

  const inputRef = useRef(null);
  const {
    animationsEnabled,
    qrScanEnabled,
    setQrScanEnabled,
    cardScanEnabled,
    setCardScanEnabled,
  } = useAnimationToggle();
  const swipeAnimationMode = !qrScanEnabled ? "card" : !cardScanEnabled ? "qr" : "both";

  // Card readers append an Enter keypress after a swipe, which submits the
  // form normally. The QR reader types the token as the same kind of rapid
  // keyboard-wedge burst but never sends Enter, so nothing triggers a
  // submit. This timer auto-submits once a complete value has been typed
  // and no further characters arrive for a short pause.
  const autoSubmitTimeoutRef = useRef(null);

  useDocumentTitle("Kiosk Login – Pinpoint");

  useEffect(() => {
    if (currLab?.id === "Error") {
      showToast("error", currLab.name, { persistent: true });
    }
  }, [currLab, showToast]);

  useEffect(() => {
    return () => {
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current);
      }
    };
  }, []);

  // Checks if user is swiped in or not and routes them to the appropriate page
  async function processSwipe(e, overrideInput) {
    e.preventDefault();
    if (autoSubmitTimeoutRef.current) {
      clearTimeout(autoSubmitTimeoutRef.current);
      autoSubmitTimeoutRef.current = null;
    }
    if (isLoading) return;
    const swipeValue = overrideInput || input;
    setIsLoading(true);
    // QR codes are scanned into the same hidden input as a card swipe —
    // branch on shape before falling through to the digit-only swipe path.
    if (Validators.looksLikeQrToken(swipeValue)) {
      processQrLogin(swipeValue);
      setCurrLab(currLab);
      return;
    }
    // ensure that the input is in proper format
    if (Validators.validateSwipeNum(swipeValue)) {
      // convert swipe number to UID
      timeout(8000, Backend.convertSwipeNumToUID(swipeValue))
        .then((res_convert) => {
          setUID(res_convert.UID);
          // get user info from UID — skip the call if lookup already failed
          const userLookup = res_convert.status === 200
            ? Backend.getUser(res_convert.UID)
            : Promise.resolve({ status: res_convert.status });
          timeout(8000, userLookup)
            .then((res_getuser) => {
              // Check if UID from swipenum is in db. If it is, proceed to machine selection page.
              // If it isn't, proceed to registration screen
              if (res_getuser.status === 200 && res_convert.status === 200) {
                // Pass swipe number along with user data for use in CustomReasons swipe-in
                onSubmit({ ...res_getuser, swipeNum: swipeValue });
                popupIfUserAutoSwipedOut(res_convert.UID, currLab.id);
              } else if (res_convert.status === 404) {
                Backend.adminReportFailedSwipe(currLab.id, {
                  swipe_number: swipeValue, failure_stage: "user_lookup",
                  failure_reason: "Card not found in UMD system", http_status: 404,
                }).catch(() => {});
                flashErrorMessage("Card could not be found in the UMD system. Please swipe a valid university ID card.");
              } else if (res_convert.status === 401) {
                Backend.adminReportFailedSwipe(currLab.id, {
                  swipe_number: swipeValue, failure_stage: "user_lookup",
                  failure_reason: "University API error", http_status: 401,
                }).catch(() => {});
                flashErrorMessage("The university API experienced an issue. If the issue persists, please contact eds@umd.edu.");
              } else if (res_convert.status === 503) {
                // The UMD ID-card lookup couldn't be completed at all (upstream
                // outage, timeout, or a rejected credential — the backend's
                // `error` reason code says which, and is recorded for admins
                // here but never shown to the person swiping). Nothing about
                // their card or their account is wrong, so registering again
                // cannot help and must not be suggested.
                Backend.adminReportFailedSwipe(currLab.id, {
                  swipe_number: swipeValue, failure_stage: "user_lookup",
                  failure_reason: res_convert.error || "University ID card lookup unavailable",
                  http_status: 503,
                }).catch(() => {});
                flashErrorMessage("The university ID card lookup is unavailable right now, so we can't sign you in. This isn't a problem with your card or your account, and creating a new account won't help. Please try again shortly, and contact eds@umd.edu if the issue persists.");
              } else if (res_convert.status === 429 || res_getuser.status === 429) {
                Backend.adminReportFailedSwipe(currLab.id, {
                  swipe_number: swipeValue, failure_stage: "user_lookup",
                  failure_reason: "Rate limited", http_status: 429,
                }).catch(() => {});
                flashErrorMessage("Too many swipes in a short time. Please wait a moment and try again.");
              } else if (res_convert.status !== 200) {
                Backend.adminReportFailedSwipe(currLab.id, {
                  swipe_number: swipeValue, failure_stage: "user_lookup",
                  failure_reason: "Unknown error during swipe number conversion",
                  http_status: res_convert.status,
                }).catch(() => {});
                flashErrorMessage("We were unable to process your request. Please try again, and contact eds@umd.edu if the issue persists.");
              } else {
                onSubmit({ status: res_convert.status, UID: res_convert.UID });
                typeof routeFunc === "function" && history.push(kioskLocation("/kiosk/register", history.location)); // go to register page
              }
              setIsLoading(false);
            })
            .catch((e) => {
              Backend.adminReportFailedSwipe(currLab.id, {
                swipe_number: swipeValue, failure_stage: "user_lookup",
                failure_reason: "Request timeout or network error",
                error_message: e?.message,
              }).catch(() => {});
              flashErrorMessage(
                "We were unable to process your request. Please try again, and contact eds@umd.edu if the issue persists."
              );
            });
        })
        .catch((e) => {
          Backend.adminReportFailedSwipe(currLab.id, {
            swipe_number: swipeValue, failure_stage: "user_lookup",
            failure_reason: "Request timeout or network error",
            error_message: e?.message,
          }).catch(() => {});
          flashErrorMessage(
            "We were unable to process your request. Please try again, and contact eds@umd.edu if the issue persists."
          );
        });
    } else {
      Backend.adminReportFailedSwipe(currLab.id, {
        swipe_number: swipeValue, failure_stage: "frontend_validation",
        failure_reason: "Swipe number failed format validation (not 15-18 digits)",
      }).catch(() => {});
      flashErrorMessage(
        "Card could not be found in the UMD system. Please swipe a valid university ID card."
      );
    }
    setCurrLab(currLab);
  }

  // Redacts a QR token before it's logged to the (admin-visible) failed-swipe
  // audit trail. Unlike a card's swipe number, a QR token is a live,
  // single-use login credential for as long as it hasn't been redeemed —
  // e.g. it's still valid after a 429 or a network error that may not have
  // reached the server. Keep a short prefix/suffix for correlation without
  // handing out a working credential to whoever reads that log.
  function redactQrToken(token) {
    return token.length > 12 ? `${token.slice(0, 8)}…${token.slice(-4)}` : token;
  }

  // Redeems a scanned QR token the same way processSwipe resolves a swipe
  // number to a UID, then hands off to the existing session-creation flow.
  function processQrLogin(token) {
    timeout(8000, Backend.redeemQrLoginToken(token, currLab.id))
      .then((res) => {
        if (res.status === 200) {
          onSubmit(res);
          popupIfUserAutoSwipedOut(res.UID, currLab.id);
        } else if (res.status === 410) {
          Backend.adminReportFailedSwipe(currLab.id, {
            swipe_number: redactQrToken(token), failure_stage: "qr_login_redeem",
            failure_reason: "QR code invalid, expired, or already used", http_status: 410,
          }).catch(() => {});
          flashErrorMessage(
            "This QR code has expired or already been used. Please generate a new one from your profile."
          );
        } else if (res.status === 429) {
          Backend.adminReportFailedSwipe(currLab.id, {
            swipe_number: redactQrToken(token), failure_stage: "qr_login_redeem",
            failure_reason: "Rate limited", http_status: 429,
          }).catch(() => {});
          flashErrorMessage("Too many attempts in a short time. Please wait a moment and try again.");
        } else {
          Backend.adminReportFailedSwipe(currLab.id, {
            swipe_number: redactQrToken(token), failure_stage: "qr_login_redeem",
            failure_reason: "Unknown error during QR redemption", http_status: res.status,
          }).catch(() => {});
          flashErrorMessage(
            "We were unable to process your request. Please try again, and contact eds@umd.edu if the issue persists."
          );
        }
        setIsLoading(false);
      })
      .catch((e) => {
        Backend.adminReportFailedSwipe(currLab.id, {
          swipe_number: redactQrToken(token), failure_stage: "qr_login_redeem",
          failure_reason: "Request timeout or network error",
          error_message: e?.message,
        }).catch(() => {});
        flashErrorMessage(
          "We were unable to process your request. Please try again, and contact eds@umd.edu if the issue persists."
        );
      });
  }

  function popupIfUserSwipedIn(UID, labID) {
    Backend.isUserSwipedIn(UID, labID).then((swiped_in) => {
      if (swiped_in) {
        setShowChoicePopup(true); // prompt user to swipe out or reserve more machines with SwipeOrReserve component
      } else {
        // Swipe-in moved to CustomReasons.jsx to avoid counting Adminland access as a swipe
        typeof routeFunc === "function" &&
        history.push(kioskLocation("/kiosk/check-in/purpose", history.location)); // go to custom reasons page
      }
    });
  }

  // Function to tell if a user was automatically swiped out last time they used this lab
  function popupIfUserAutoSwipedOut(UID, labID) {
    Backend.isLastSwipeOutAuto(UID).then((count) => {
      if (count) {
        setReminderCount(count);
        setShowReminderPopup(true);
      } else {
        // Open the "sign out or reserve new tools" popup if needed
        popupIfUserSwipedIn(UID, labID);
      }
    });
  }

  // Used to create time out messages
  function timeout(ms, promise) {
    return new Promise(function (resolve, reject) {
      setTimeout(function () {
        reject(
          new Error("We were unable to process your request. Please try again, and contact eds@umd.edu if the issue persists.")
        );
      }, ms);
      promise.then(resolve, reject);
    });
  }

  function flashErrorMessage(message) {
    setIsLoading(false);
    setInput("");
    showToast("error", message);
  }

  function handleDevSimulate(mode, value) {
    if (mode === "uid") {
      setIsLoading(true);
      timeout(8000, Backend.getUser(value))
        .then((res_getuser) => {
          if (res_getuser.status === 200) {
            setUID(value);
            onSubmit({ ...res_getuser, swipeNum: "000000000000000" });
            popupIfUserAutoSwipedOut(value, currLab.id);
          } else {
            onSubmit({ status: res_getuser.status, UID: value });
            typeof routeFunc === "function" && history.push(kioskLocation("/kiosk/register", history.location));
          }
          setIsLoading(false);
        })
        .catch(() => {
          flashErrorMessage(
            `Dev tools: unable to find user with UID ${value}. Check that the user exists in the database.`
          );
          setIsLoading(false);
        });
    } else {
      processSwipe({ preventDefault: () => {} }, value);
    }
  }

  return (
    <Flex
      onClick={() => {
        inputRef.current.focus();
      }}
      sx={{ touchAction: "manipulation" }}
      position="relative"
      bg="gray.50"
      minH="100vh"
      align="center"
      justify="center"
      direction="column"
      px={8}
    >
          <Image
            w={20}
            src={pinpointLogo}
            alt="Pinpoint logo"
            mb={16}
          />
          <Box
            bg="white"
            rounded="xl"
            boxShadow="lg"
            py={8}
            px={10}
            mx="auto"
            maxW="540px"
            w="full"
          >
            <VStack spacing={0} align="center">
              <Text fontSize="xl" color="gray.600">
                Welcome to
              </Text>
              <Heading fontSize="4xl" fontWeight="bold" color="gray.800" mb={2}>
                {currLabName}
              </Heading>

              <form onSubmit={processSwipe} style={{ width: "100%" }}>
                <Flex justify="center" align="center" pt={2} pb={6} minH="200px">
                  {isLoading ? (
                    <KioskLoadingAnimation />
                  ) : (
                    <KioskSwipeInstructionSVG animate={animationsEnabled} mode={swipeAnimationMode} />
                  )}
                </Flex>

                <FormControl id="UID">
                  <Input
                    ref={inputRef}
                    type="password"
                    autoFocus={true}
                    autoComplete="off"
                    name="kiosk-swipe-input"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-form-type="other"
                    outline="none"
                    border="none"
                    textColor="transparent"
                    focusBorderColor="none"
                    h={0}
                    p={0}
                    position="absolute"
                    opacity={0}
                    value={input}
                    onChange={(e) => {
                      // Card swipes are digit-only; QR tokens are
                      // "QR1-" + url-safe base64 (letters/digits/-/_).
                      // Accept both shapes here and let processSwipe branch
                      // on which one this actually is.
                      const match = e.target.value.match(/^[A-Za-z0-9_-]*/);
                      if (!match) return;
                      const newValue = match[0];
                      setInput(newValue);

                      if (autoSubmitTimeoutRef.current) {
                        clearTimeout(autoSubmitTimeoutRef.current);
                        autoSubmitTimeoutRef.current = null;
                      }
                      // Only auto-submit QR tokens, not swipe numbers: a
                      // card reader already reliably sends its own Enter
                      // right after the last digit, so that path doesn't
                      // need this timer at all. Swipe numbers are also a
                      // poor fit for it — valid lengths span 15-18 digits,
                      // so a mid-swipe pause could make a 15-digit prefix
                      // of a real 18-digit card number look "complete" and
                      // fire early. QR tokens don't have that ambiguity
                      // (fixed shape/prefix) and are the one case with no
                      // trailing Enter to rely on.
                      if (Validators.validateQrToken(newValue)) {
                        autoSubmitTimeoutRef.current = setTimeout(() => {
                          autoSubmitTimeoutRef.current = null;
                          processSwipe({ preventDefault: () => {} }, newValue);
                        }, 150);
                      }
                    }}
                  />
                </FormControl>
              </form>

              <Text
                fontSize="2xl"
                fontWeight={500}
                color="gray.500"
                textAlign="center"
              >
                {swipeAnimationMode === "qr"
                  ? "Scan your QR code to check in."
                  : swipeAnimationMode === "card"
                  ? "Swipe your ID card to check in."
                  : "Swipe your ID card or scan your QR code to check in."}
              </Text>
            </VStack>
          </Box>
        {version && environment && (
          <Text fontSize="0.625rem" color="gray.400" position="absolute" bottom={2}>
            Pinpoint v{version} &middot; {environment}
          </Text>
        )}
        {toastElements}
        {showReminderPopup && (
          <SwipeOutReminderPopup
            isOpen={showReminderPopup}
            count={reminderCount}
            onClose={() => {
              setShowReminderPopup(false);
            }}
            onCheckout={() => {
              // Swipe-in moved to CustomReasons.jsx to avoid counting Adminland access as a swipe
            }}
          />
        )}
        {showChoicePopup && (
          <SwipeOrReservePopup
            isOpen={showChoicePopup}
            onClose={() => {
              setShowChoicePopup(false);
            }}
            onCheckOut={async () => {
              const res = await Backend.userSwipeOut(UID, currLab.id).catch(() => null);
              if (!res || res.status >= 400) {
                showToast("error", "Checkout failed — please try again or contact space staff.");
                return false;
              }
            }}
          />
        )}
        <KioskAnimationSettings
          qrScanEnabled={qrScanEnabled}
          setQrScanEnabled={setQrScanEnabled}
          cardScanEnabled={cardScanEnabled}
          setCardScanEnabled={setCardScanEnabled}
          bottom={isNonProduction ? 20 : 4}
        />
        {isNonProduction && (
          <DevToolsPanel isLoading={isLoading} onSimulate={handleDevSimulate} />
        )}
    </Flex>
  );
}

KioskLogin.propTypes = {
  onSubmit: PropTypes.func,
};

export default withRouter(KioskLogin);
